// src/components/layout/Sidebar.jsx
import React from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { AlertCircle, LayoutGrid, Shield, Star, Users, Wrench } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTools } from '../../hooks/useTools';
import { useCategories } from '../../hooks/useCategories';
import { useFavorites } from '../../hooks/useFavorites';
import { UNCATEGORISED_KEY, isUncategorised } from '../../config/categories';
import { cx } from '../common/ui';

const itemClass = (active) =>
  cx(
    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-sm transition-colors',
    active
      ? 'bg-accent-soft font-medium text-accent'
      : 'text-ink-2 hover:bg-surface-2 hover:text-ink'
  );

const Group = ({ label, children }) => (
  <div className="flex flex-col gap-0.5">
    <p className="px-2.5 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.11em] text-ink-3">
      {label}
    </p>
    {children}
  </div>
);

const Count = ({ children, active }) => (
  <span className={cx('tnum ml-auto font-mono text-xs', active ? 'text-accent' : 'text-ink-3')}>
    {children}
  </span>
);

const Sidebar = ({ onNavigate }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Both already cached by React Query — the catalog fetched them first.
  const { tools } = useTools();
  const { categories } = useCategories();
  const { favorites } = useFavorites();

  const onCatalog = location.pathname === '/dashboard';
  const activeCat = onCatalog ? searchParams.get('cat') || 'all' : null;

  const countFor = (key) => tools.filter((t) => t.category === key).length;
  const favCount = tools.filter((t) => favorites.includes(t._id)).length;
  const strayCount = tools.filter((t) => isUncategorised(categories, t)).length;

  const browseLink = (cat, label, Icon, count) => {
    const active = activeCat === cat;
    return (
      <NavLink
        to={cat === 'all' ? '/dashboard' : `/dashboard?cat=${cat}`}
        onClick={onNavigate}
        className={itemClass(active)}
        aria-current={active ? 'page' : undefined}
      >
        <Icon className="h-[17px] w-[17px] flex-none" />
        {label}
        <Count active={active}>{count}</Count>
      </NavLink>
    );
  };

  return (
    <nav className="flex h-full flex-col gap-5 overflow-y-auto border-r border-line bg-surface px-3.5 py-5 lg:bg-transparent">
      <Group label="Browse">
        {browseLink('fav', 'Favourites', Star, favCount)}
        {browseLink('all', 'All tools', LayoutGrid, tools.length)}
      </Group>

      {categories.length > 0 && (
        <Group label="Categories">
          {categories.map((category) => {
            const active = activeCat === category.key;
            return (
              <NavLink
                key={category.key}
                to={`/dashboard?cat=${category.key}`}
                onClick={onNavigate}
                className={itemClass(active)}
                aria-current={active ? 'page' : undefined}
              >
                <span className="cat-dot" data-color={category.color} />
                <span className="truncate">{category.label}</span>
                <Count active={active}>{countFor(category.key)}</Count>
              </NavLink>
            );
          })}

          {strayCount > 0 && (
            <NavLink
              to={`/dashboard?cat=${UNCATEGORISED_KEY}`}
              onClick={onNavigate}
              className={itemClass(activeCat === UNCATEGORISED_KEY)}
              aria-current={activeCat === UNCATEGORISED_KEY ? 'page' : undefined}
            >
              <AlertCircle className="h-[17px] w-[17px] flex-none" />
              <span className="truncate">Uncategorised</span>
              <Count active={activeCat === UNCATEGORISED_KEY}>{strayCount}</Count>
            </NavLink>
          )}
        </Group>
      )}

      {user.role === 'admin' && (
        <Group label="Admin">
          <NavLink to="/admin/tools" onClick={onNavigate} className={({ isActive }) => itemClass(isActive)}>
            <Wrench className="h-[17px] w-[17px] flex-none" />
            Tools
          </NavLink>
          <NavLink to="/admin/users" onClick={onNavigate} className={({ isActive }) => itemClass(isActive)}>
            <Users className="h-[17px] w-[17px] flex-none" />
            People
          </NavLink>
          <NavLink to="/admin/roles" onClick={onNavigate} className={({ isActive }) => itemClass(isActive)}>
            <Shield className="h-[17px] w-[17px] flex-none" />
            Roles
          </NavLink>
        </Group>
      )}

      <p className="mt-auto px-2.5 text-xs leading-relaxed text-ink-3">
        You only ever see tools your role allows. Missing something? Ask an admin.
      </p>
    </nav>
  );
};

export default Sidebar;
